import React, { useState, useRef, useCallback, useEffect } from 'react';
import './ResumeUploader.css';

function ResumeUploader({
  onUpload,
  uploading = false,
  parsing = false,
  parseProgress = 0,
  parseMessage = '',
  uploaded = false,
  error: externalError = null
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [localError, setLocalError] = useState(null);
  const inputRef = useRef(null);

  // Combine local and external errors
  const error = externalError || localError;

  // Clear local error when external error changes
  useEffect(() => {
    if (externalError) {
      setLocalError(null);
    }
  }, [externalError]);

  const validateFile = async (file) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const maxSize = 10 * 1024 * 1024; // 10MB

    // Check file extension
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['pdf', 'doc', 'docx'];
    if (!allowedExtensions.includes(ext)) {
      return 'Please upload a PDF or Word document (.pdf, .doc, .docx)';
    }

    // Check MIME type
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Please upload a PDF or Word document';
    }

    // Check file size
    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }

    // Validate magic bytes for additional security
    try {
      const headerBytes = await readFileHeader(file, 8);
      if (!isValidFileHeader(headerBytes, ext)) {
        return 'File content does not match its extension. Please upload a valid document';
      }
    } catch {
      // If we can't read the header, let the server validate
      console.warn('Could not validate file header');
    }

    return null;
  };

  // Read first N bytes of file
  const readFileHeader = (file, bytes) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(new Uint8Array(reader.result));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file.slice(0, bytes));
    });
  };

  // Validate file magic bytes
  const isValidFileHeader = (bytes, ext) => {
    if (ext === 'pdf') {
      // PDF files start with %PDF
      return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
    }
    if (ext === 'docx') {
      // DOCX files are ZIP archives, start with PK
      return bytes[0] === 0x50 && bytes[1] === 0x4B;
    }
    if (ext === 'doc') {
      // DOC files start with D0 CF 11 E0 (OLE compound document)
      return bytes[0] === 0xD0 && bytes[1] === 0xCF && bytes[2] === 0x11 && bytes[3] === 0xE0;
    }
    return true; // Unknown extension, let server validate
  };

  const handleFile = useCallback(async (file) => {
    const validationError = await validateFile(file);
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError(null);
    // Sanitize filename for display (remove path components, limit length)
    const sanitizedName = file.name.replace(/[<>:"/\\|?*]/g, '').slice(0, 100);
    setFileName(sanitizedName);
    onUpload(file);
  }, [onUpload]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  };

  return (
    <div className="resume-uploader">
      <div
        className={`upload-zone ${isDragging ? 'dragging' : ''} ${uploaded ? 'uploaded' : ''} ${error ? 'error' : ''}`}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={handleChange}
          style={{ display: 'none' }}
        />

        {uploading ? (
          <div className="upload-state">
            <div className="upload-spinner" />
            <p className="upload-text">Uploading...</p>
            <p className="upload-subtext">Uploading your resume</p>
          </div>
        ) : parsing ? (
          <div className="upload-state parsing">
            <div className="upload-spinner" />
            <p className="upload-text">Analyzing with AI...</p>
            <p className="upload-subtext">{parseMessage || 'Processing your resume'}</p>
            <div className="parse-progress-container">
              <div className="parse-progress-bar" style={{ width: `${parseProgress}%` }} />
            </div>
            <p className="parse-progress-text">{parseProgress}% complete</p>
          </div>
        ) : uploaded ? (
          <div className="upload-state success">
            <div className="upload-icon success">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M26 10L13 23L6 16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="upload-text">{fileName || 'Resume uploaded!'}</p>
            <p className="upload-subtext">Your resume has been processed</p>
            <button
              type="button"
              className="change-file-btn"
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
            >
              Change file
            </button>
          </div>
        ) : (
          <div className="upload-state">
            <div className="upload-icon">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M28 20V26C28 27.1046 27.1046 28 26 28H6C4.89543 28 4 27.1046 4 26V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 10L16 4L10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 4V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="upload-text">
              {isDragging ? 'Drop your resume here' : 'Drag & drop your resume'}
            </p>
            <p className="upload-subtext">or click to browse (PDF or Word, max 10MB)</p>
          </div>
        )}
      </div>

      {error && (
        <p className="upload-error">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 5V8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="8" cy="11" r="1" fill="currentColor"/>
          </svg>
          {error}
        </p>
      )}

      <p className="upload-hint">
        We'll extract your experience, skills, and education to save you time.
      </p>
    </div>
  );
}

export default ResumeUploader;
